import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { useUnsavedChanges } from '@/context/UnsavedChangesContext'
import { getRelease } from '@/lib/env'
import { logger } from '@/lib/logger'
import { versionReloadGuard } from '@/services/versionReloadGuard.service'

import { reloadWithGuard } from './reloadWithGuard'
import { DEV_VERSION, POLL_INTERVAL_MS, THROTTLE_MS, isOutdated, parseVersionManifest } from './versionFreshness'

// Headless app-freshness checker, mounted as a sibling of <RouteAnalytics/> at
// the router root so it sees every navigation. Polls /version.json on
// navigation (throttled) and on a background interval (paused while the tab
// is hidden), then triggers a guarded reload once a newer release is detected
// AND there are no unsaved changes. Renders nothing.
export function AppFreshnessGuard() {
  const bakedVersion = getRelease()
  // null = no manifest has been fetched yet. Kept distinct from the baked value so a
  // fresh mount never looks "converged" and clears the reload budget before checking.
  const [latestVersion, setLatestVersion] = useState<string | null>(null)
  const { pathname } = useLocation()
  const { hasUnsavedChanges } = useUnsavedChanges()

  const inFlightRef = useRef<Promise<void> | null>(null)
  const lastCheckedAtRef = useRef(0)
  const isReloadingRef = useRef(false)

  function requestCheck(): Promise<void> {
    if (bakedVersion === DEV_VERSION) return Promise.resolve()
    if (inFlightRef.current) return inFlightRef.current

    const now = Date.now()
    if (now - lastCheckedAtRef.current < THROTTLE_MS) return Promise.resolve()
    lastCheckedAtRef.current = now

    const check = fetch('/version.json', { cache: 'no-store' })
      .then(response => response.json())
      .then((json: unknown) => {
        const version = parseVersionManifest(json)
        if (version !== null) setLatestVersion(version)
      })
      .catch((error: unknown) => {
        logger.warn('Failed to check app version freshness:', error)
      })
      .finally(() => {
        inFlightRef.current = null
      })

    inFlightRef.current = check
    return check
  }

  useEffect(() => {
    void requestCheck()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  useEffect(() => {
    function handleVisibilityChange(): void {
      if (!document.hidden) void requestCheck()
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    const intervalId = setInterval(() => {
      if (document.hidden) return
      void requestCheck()
    }, POLL_INTERVAL_MS)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      clearInterval(intervalId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    // No manifest fetched yet — don't touch the reload budget. Clearing here (on the
    // pre-fetch state where latestVersion === baked) would reset attempts on every
    // mount and let a stuck deploy hard-reload forever.
    if (latestVersion === null) return
    if (!isOutdated(bakedVersion, latestVersion)) {
      // A fetch confirmed we're running the current release — reset the budget so the
      // next deploy starts fresh.
      versionReloadGuard.clear()
      return
    }
    if (hasUnsavedChanges) return

    const blockedForMs = reloadWithGuard(latestVersion, isReloadingRef)
    // Cooldown-blocked (e.g. attempt 1 reloaded but the deploy overlap still served
    // the old bundle): the fetched version won't change, so nothing else re-runs this
    // effect. Schedule the next attempt ourselves once the cooldown elapses.
    if (blockedForMs > 0) {
      const retryId = setTimeout(() => reloadWithGuard(latestVersion, isReloadingRef), blockedForMs)
      return () => clearTimeout(retryId)
    }
  }, [bakedVersion, latestVersion, hasUnsavedChanges])

  return null
}
